import type { User } from '@integration-layer/server/utils/auth0Utils.ts'

/**
 * Composable for managing user metadata and authentication state.
 * Provides methods to initialize, refresh, and access user metadata.
 *
 * @returns {Object} An object containing user metadata and related methods
 * @property {ComputedRef<TUserMeta>} userMeta - Computed user metadata
 * @property {ComputedRef<User | null>} userData - Computed user data
 * @property {Function} initUserMeta - Method to initialize user metadata
 * @property {Function} refreshUserMeta - Method to refresh user metadata
 */
export const useUserMeta = () => {
  // Unique key for caching and state management
  const key = 'useUserMeta'

  const nuxtApp = useNuxtApp()

  // Fetch configuration for retrieving user metadata
  const { refresh } = useFetch('/api/auth0/getUserMetadata', {
    key,
    immediate: false,
    server: false,
    dedupe: 'defer',
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
  })

  // Reactive state for user metadata
  const userMetaState = useState<User | null>(key, () => null)

  /**
   * Initializes user metadata if not already loaded.
   * Fetches user metadata from the server and updates the state.
   *
   * @async
   */
  const initUserMeta = async () => {
    if (userMetaState.value) return
    await refresh()
    userMetaState.value = nuxtApp.payload.data[key]
  }

  /**
   * Refreshes user metadata by fetching the latest data from the server.
   *
   * @async
   */
  const refreshUserMeta = async () => {
    await refresh({ _initial: false, dedupe: 'cancel' })
    userMetaState.value = nuxtApp.payload.data[key]
  }

  // Computed property for user metadata
  const userMeta = computed(
    () => userMetaState.value?.user_metadata as TUserMeta
  )

  // Computed property for complete user data
  const userData = computed(() => userMetaState.value)

  return {
    userMeta,
    userData,
    initUserMeta,
    refreshUserMeta,
  }
}
